import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialogActions, MatDialogClose, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeButtonModule, ForgeLinearProgressModule } from "@tylertech/forge-angular";
import { LgdDatePickerComponent, LgdFormControlFactory, LgdSelectComponent, LgdTextComponent, LgdTypeAheadComponent, setEnabled } from "@tylertech/lgd-reactive-forms";
import { InputParameterService } from "@tylertech/search-module";
import { Utilities } from "@tylertech/shared-module";
import { LookupCriteria, LookupItemViewModel } from "@tylertech/tyler-api-types";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TodoItemDTO } from "../../interfaces";
import { EntityLookup, EntitySuggestService, UBDashboardService } from "../../services";

@Component({
    selector: "add-todo-item-dialog-component",
    templateUrl: "./addTodoItemDialog.tmpl.html",
    styleUrls: [
        "./addTodoItemDialog.component.scss"
    ],
    imports: [
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        ForgeLinearProgressModule,
        LgdDatePickerComponent,
        LgdSelectComponent,
        LgdTextComponent,
        LgdTypeAheadComponent,
        MatDialogActions,
        MatDialogClose,
        MatDialogTitle,
        ReactiveFormsModule,
    ]
})
export class AddTodoItemDialogComponent implements OnDestroy, OnInit
{
    private readonly dialogRef = inject<MatDialogRef<AddTodoItemDialogComponent, TodoItemDTO | undefined>>(MatDialogRef);
    private readonly $inputParameterService = inject(InputParameterService);
    private readonly ubDashboardService = inject(UBDashboardService);
    private readonly entitySuggestService = inject(EntitySuggestService);

    private readonly destructor$ = new Subject<void>();
    readonly entityTypesSubject = new BehaviorSubject<ReadonlyArray<EntityLookup>>([]);
    readonly entitiesSubject = new BehaviorSubject<ReadonlyArray<LookupItemViewModel>>([]);
    readonly formGroup: FormGroup;

    isSaving: boolean;

    constructor()
    {
        this.formGroup = new FormGroup({
            entityType: LgdFormControlFactory.select<EntityLookup>(
                undefined,
                true,
                {
                    displayValueFn: x => x.value,
                }),
            customEntity: LgdFormControlFactory.text(
                undefined,
                () => this.isCustom,
                {
                    maxLength: 100
                }),
            entity: LgdFormControlFactory.typeahead<LookupItemViewModel>(
                undefined,
                () => !this.isCustom,
                {
                    displayValueFn: x => x ? x.Description : ""
                }),
            note: LgdFormControlFactory.text(),
            dueDate: LgdFormControlFactory.date(new Date(Date.now()), true),
        });
    }

    private get entityTypeControl(): AbstractControl<EntityLookup> { return this.formGroup?.get("entityType"); }
    private get customEntityControl(): AbstractControl<string> { return this.formGroup?.get("customEntity"); }
    private get entityControl(): AbstractControl<LookupItemViewModel> { return this.formGroup?.get("entity"); }
    private get noteControl(): AbstractControl<string> { return this.formGroup?.get("note"); }
    private get dueDateControl(): AbstractControl<Date> { return this.formGroup?.get("dueDate"); }

    get entityTypesObservable(): Observable<ReadonlyArray<EntityLookup>> { return this.entityTypesSubject.asObservable(); }
    get entitiesObservable(): Observable<ReadonlyArray<LookupItemViewModel>> { return this.entitiesSubject.asObservable(); }

    get isCustom(): boolean { return this.entityTypeControl?.value?.key === "Custom"; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.entitySuggestService.getRegisteredEntities()
            .then((result: ReadonlyArray<EntityLookup>) =>
            {
                this.entityTypesSubject.next(result);
            });

        subscribeToValueChanges(this.entityTypeControl, (newValue: EntityLookup) =>
        {
            setEnabled(this.customEntityControl, !!newValue);
            setEnabled(this.entityControl, !!newValue);

            this.customEntityControl.setValue(undefined);
            this.entityControl.setValue(undefined);
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    add(): void
    {
        if (!this.formGroup.valid)
        {
            return;
        }

        this.isSaving = true;
        const item: TodoItemDTO = {
            EntityType: this.entityTypeControl.value.key,
            EntityId: !this.isCustom
                ? this.entityControl.value.Id
                : undefined,
            EntityNumber: this.isCustom
                ? this.customEntityControl.value
                : this.entityControl.value.Description,
            Completed: false,
            ItemDescription: this.noteControl.value,
            DueDate: Utilities.toShortDateString(this.dueDateControl.value),
        };

        this.ubDashboardService.saveTodoItem(item)
            .then(result =>
            {
                this.isSaving = false;

                this.dialogRef.close(result);
            });
    }

    lookupEntities(searchText: string): void
    {
        if (!searchText) return;
        this.entitySuggestService.getTodoEntitySuggestions(
            <LookupCriteria>{
                SearchText: searchText,
                Parameters: [
                    this.$inputParameterService.createTextParameter({
                        Label: "entityKey",
                        FieldId: "entityKey",
                        Value: this.entityTypeControl?.value.key,
                    })
                ]
            })
            .then((results: LookupItemViewModel[]) =>
            {
                this.entitiesSubject.next(results);
            });
    }
}
